<script setup lang="ts">
import { ref, defineExpose } from 'vue';
import type { HTMLAttributes } from "vue";
import { useVModel } from "@vueuse/core";
import { cn } from "@/utils";
import type { HTMLInputElementExtended } from "@/types";
// Define an interface that extends HTMLInputElement with a shake method.


const props = defineProps<{
  defaultValue?: string | number;
  modelValue?: string | number;
  class?: HTMLAttributes["class"];
}>();

const emits = defineEmits<{
  (e: "update:modelValue", payload: string | number): void;
}>();

const modelValue = useVModel(props, "modelValue", emits, {
  passive: true,
  defaultValue: props.defaultValue,
});

const inputRef = ref<HTMLInputElement | null>(null);

const focus = () => {
  inputRef.value?.focus();
};

const shake = () => {
  const el = inputRef.value;
  if (el) {
    el.classList.add('shake');
    setTimeout(() => el.classList.remove('shake'), 500);
  }
};

// Create an object that implements our extended interface.
// Note that while the underlying input element already provides focus,
// we add the shake method to extend its functionality.
const exposed = { focus, shake } as HTMLInputElementExtended;

defineExpose(exposed);
</script>

<template>
  <input
    ref="inputRef"
    v-model="modelValue"
    :class="cn(
      'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
      props.class
    )"
  />
</template>
